/**
 * Look at a client version on the Review tab (admin only): its files as the
 * server signed them for 10 minutes (lookVersion,
 * functions/src/zelma/services/versionLook.ts), and the Suite's own viewer
 * (modelViewer.js, loaded only when a model is opened) for meshes (GLB,
 * glTF, OBJ, PLY), point clouds and splats (SPZ, splat PLY). A file the
 * viewer cannot show says why and stays in the list. Looking changes nothing:
 * approval is its own click on the card.
 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "./DeliveryReview.jsx";
import { linksFresh, lookErrorText, notShownWhy, sizeOf, viewableFiles } from "./modelLook.js";

const VIEWER_WORDS = {
  no_webgl: "This browser can't draw 3D here (WebGL is off or missing). The files are listed beside.",
  download_failed: "The download stopped. Try again, or open the file from the list beside.",
  link_expired: "The link ran out. Try again: a new one is asked for.",
  unreadable: "The viewer could not read this file. Its files are listed beside.",
  draco: "This GLB is compressed in a way the viewer can't read (Draco). Open it on a computer.",
  too_large: "This model is too large to build in the browser. Open the file on a computer.",
  unsupported_model: "This model uses a feature the viewer does not open here (animation, skins, instancing, several scenes, or a compression it can't read). Open the file on a computer.",
  unsupported_image: "This model's textures are in a format the viewer does not open here (only PNG and JPEG). Open the file on a computer.",
  not_viewable: "This kind of file can't be shown here.",
  no_link: "This file has no copy to open yet.",
};
export const viewerErrorText = (e) => VIEWER_WORDS[e?.code] ?? VIEWER_WORDS.unreadable;

/** The file list under a card, once Look answered (pure render: exported for the test). */
export function LookFiles({ look, onOpen }) {
  if (!look) return null;
  const files = look.files ?? [];
  const viewable = new Set(viewableFiles(files).map((f) => f.name));
  return (
    <div className="rv-look" aria-label="Files of this version">
      {files.length === 0 && <span className="rv-line muted">No files to open yet.</span>}
      {files.map((f) => {
        const why = notShownWhy(f);
        return (
          <div key={f.name} className="rv-look-row">
            <span className="rv-look-name mono" title={f.name}>{f.name}</span>
            <span className="rv-muted">{sizeOf(f.bytes)}</span>
            {viewable.has(f.name)
              ? <button type="button" className="rv-link" onClick={() => onOpen(f.name)}>Open in viewer</button>
              : <span className="rv-muted">{why}</span>}
            {f.url && <a className="rv-link" href={f.url} target="_blank" rel="noreferrer noopener" referrerPolicy="no-referrer">File</a>}
          </div>
        );
      })}
      {look.missing > 0 && (
        <span className="rv-line muted">{look.missing} {look.missing === 1 ? "file has" : "files have"} no copy to open yet.</span>
      )}
      {files.length > 0 && viewable.size === 0 && <span className="rv-line muted">None of its files can be shown in the viewer; they are listed above.</span>}
    </div>
  );
}

const pct = (got, total) => (total > 0 ? ` (${Math.min(100, Math.floor((got / total) * 100))}%)` : "");

/**
 * The viewer, in a dialog. `getLook()` answers a look with fresh links (asked
 * again when the shown one is about to end); `name` is the file to open.
 */
export function ViewerDialog({ title, approval, name, look, getLook, onClose, loadViewer = () => import("./modelViewer.js") }) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const [stage, setStage] = useState("start");
  const [progress, setProgress] = useState([0, 0]);
  const [error, setError] = useState("");
  const [current, setCurrent] = useState(name);
  const [attempt, setAttempt] = useState(0);
  const [shown, setShown] = useState(look);

  useEffect(() => {
    const ctl = new AbortController();
    let alive = true;
    setError("");
    setStage("start");
    setProgress([0, 0]);
    (async () => {
      try {
        const fresh = linksFresh(shown) ? shown : await getLook();
        if (!fresh) return;
        if (!alive) return;
        if (fresh !== shown) setShown(fresh);
        const file = (fresh?.files ?? []).find((f) => f.name === current);
        if (!file?.url) throw Object.assign(new Error("no_link"), { code: "no_link" });
        const mod = await loadViewer();
        if (!alive || !hostRef.current) return;
        const view = await mod.openViewer(hostRef.current, {
          file, files: fresh.files, signal: ctl.signal,
          onStage: (s) => { if (alive) setStage(s); },
          onProgress: (got, total) => { if (alive) setProgress([got, total]); },
        });
        if (!alive) { view.dispose(); return; }
        viewRef.current = view;
        setStage("ready");
      } catch (e) {
        if (!alive || e?.name === "AbortError") return;
        setStage("error");
        // A viewer refusal has its code; a refusal of the look itself (fresh links) is the callable's.
        setError(e?.code ? viewerErrorText(e) : lookErrorText(e));
      }
    })();
    return () => {
      alive = false;
      ctl.abort();
      viewRef.current?.dispose();
      viewRef.current = null;
    };
    // `shown` changes only from inside this effect (fresh links): not a reason to load again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, attempt]);

  const [got, total] = progress;
  const big = (shown?.files ?? []).find((f) => f.name === current)?.bytes > 200 * 1024 ** 2;
  const loadingWords = stage === "download"
    ? `Downloading ${sizeOf(got)}${total ? ` of ${sizeOf(total)}` : ""}${pct(got, total)}…`
    : stage === "build" ? "Building the model…" : "Opening the viewer…";
  return (
    <Dialog label={`Look: ${title}`} onClose={onClose} className="rv-preview">
      <div className="rv-preview-grid">
        <div className="rv-stage rv-viewer" aria-busy={stage !== "ready" && stage !== "error"}>
          <div className="rv-canvas-host" ref={hostRef} />
          {stage !== "ready" && stage !== "error" && (
            <div className="rv-stage-ack" role="status">
              <span className="rv-spinner" aria-hidden="true" />
              <span>{loadingWords}</span>
              {big && <span>A large model: this can take a while.</span>}
            </div>
          )}
          {stage === "error" && (
            <div className="rv-stage-ack" role="alert">
              <span>{error}</span>
              <button type="button" className="rv-stage-link" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
            </div>
          )}
        </div>
        <div className="rv-side">
          <div className="rv-row" style={{ justifyContent: "space-between" }}>
            <span className="rv-side-title">{title}</span>
            <button type="button" className="rv-close" aria-label="Close" onClick={onClose}>×</button>
          </div>
          <span className="rv-line mono">{current}</span>
          <span className="rv-line muted">
            {approval === "pending" ? "Not approved: no client sees this version." : "Approved: the client can see this version."}
            {" "}Drag to turn it, scroll to zoom, right-drag to move.
          </span>
          {stage === "ready" && (
            <button type="button" className="rigv2-btn small" onClick={() => viewRef.current?.flip()}>Turn upside down</button>
          )}
          <LookFiles look={shown} onOpen={setCurrent} />
          <span className="rv-hint">Looking changes nothing. To approve, close this and use Approve on the card.</span>
        </div>
      </div>
    </Dialog>
  );
}
